import WorkScheduleHelpers from '../../../services/helpers/WorkScheduleHelpers';
import classnames from 'classnames';
import useMenu from '../../../hooks/menu/useMenu';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { OpeningHoursInterface } from '../../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';

interface Props {
  className?: string;
}

export default function MenuList({ className }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Menu:MenuList' });
  const { isMobileApp } = useMobileApp();
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { formatScheduleUnit } = useAppMethods();
  const { menus, selectedMenu, setSelectedMenu } = useMenu();

  if (menus.count() === 1) {
    return null;
  }

  if (isMobileApp) {
    return (
      <div className={classnames('w-full bg-white dark:bg-neutral-950', className)}>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2xs px-xs py-xs">
            {menus.map((item, index) => {
              const schedule = item?.schedule ?? company?.work_schedule;
              const isActive = item?.id === selectedMenu?.id;
              const isOpen = WorkScheduleHelpers.isOpen(schedule);
              const hours = WorkScheduleHelpers.formatSchedule(
                schedule,
                (s: OpeningHoursInterface) => `${formatScheduleUnit(s.open)}–${formatScheduleUnit(s.close)}`,
                (s: OpeningHoursInterface) =>
                  `${formatScheduleUnit(s.open, { day: true, time: true })}–${formatScheduleUnit(s.close, { day: true, time: true })}`,
              );

              return (
                <button
                  type="button"
                  key={`${selectedMenu?.id}-company-menu-${item?.id}-${index}`}
                  onClick={() => setSelectedMenu(item)}
                  className={classnames(
                    'shrink-0 rounded-lg px-sm py-xs text-start transition-all bg-white dark:bg-neutral-800',
                    isActive ? 'border-2 border-brand-primary' : 'border border-brand-border',
                  )}
                >
                  <h6 className="text-sm font-semibold whitespace-nowrap">{item?.name}</h6>
                  <div className="mt-[2px] flex items-center gap-[4px]">
                    <span
                      className={classnames(
                        'h-1 w-1 rounded-full shrink-0',
                        isOpen ? 'bg-green-600' : 'bg-muted-foreground/50',
                      )}
                    />
                    <span
                      className={classnames(
                        'text-[10px] whitespace-nowrap leading-tight',
                        isOpen ? 'text-green-700 font-medium' : 'text-muted-foreground',
                      )}
                    >
                      {hours}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h5>{t('header')}</h5>
      <div className={classnames('flex mt-md overflow-x-auto scrollbar-hide', className)}>
        {menus.map((item, index) => (
          <div
            key={`${selectedMenu?.id}-company-menu-${item?.id}-${index}`}
            className={classnames('mr-lg group cursor-pointer text-center rounded-lg py-2xs px-sm', {
              'bg-brand-primary text-white dark:text-black': item?.id === selectedMenu?.id,
              border: item?.id !== selectedMenu?.id,
            })}
            onClick={() => setSelectedMenu(item)}
          >
            <h6 className="text-sm uppercase">{item?.name}</h6>
            <p className="mt-2xs text-xs">
              {WorkScheduleHelpers.formatSchedule(
                item?.schedule ?? company?.work_schedule,
                (s: OpeningHoursInterface) => `${formatScheduleUnit(s.open)} - ${formatScheduleUnit(s.close)}`,
                (s: OpeningHoursInterface) =>
                  `${formatScheduleUnit(s.open, { day: true, time: true })} - ${formatScheduleUnit(s.close, { day: true, time: true })}`,
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
